import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AlertColor, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import Notification from 'components/Notification';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import strings from '../../../../common/Translation/Translate';
import axiosInstance from '../../../../utils/axios';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import {
  fetchUserContext,
  organizationInfo,
  selectedOrgId,
  setOrgInfo,
  setSelectedOrganization,
  User,
} from '../../../../utils/redux/reducer/authentication-slice';
import { createDropdownData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import CloneOrganizationDrawer from './CloneOrganization';

type DropdownOption = { label: string; value: string | number };

const stripFilterKey = (filter: unknown, key: string) => {
  const base = Array.isArray(filter)
    ? filter.filter((item: unknown) => typeof item !== 'string' || !item.startsWith(`${key}:`))
    : [];

  const cleaned: string[] = [];
  base.forEach((item: string) => {
    if (item === 'and') {
      if (cleaned.length === 0 || cleaned[cleaned.length - 1] === 'and') return;
      cleaned.push(item);
      return;
    }
    cleaned.push(item);
  });
  if (cleaned.length && cleaned[cleaned.length - 1] === 'and') cleaned.pop();
  return cleaned;
};

const withEqFilter = (filter: unknown, key: string, value: string) => {
  const cleaned = stripFilterKey(filter, key);
  if (!value) return cleaned;
  if (cleaned.length) cleaned.push('and');
  cleaned.push(`${key}:eq:${value}`);
  return cleaned;
};

const OrganizationList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const user = useSelector(User);
  const org = useSelector(organizationInfo) as any;
  const selectedOrganization = useSelector(selectedOrgId);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageResponse, setPageResponse] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [locations, setLocations] = useState<DropdownOption[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState(() => searchParams.get('locationId') || '');
  const [cloneDrawerOpen, setCloneDrawerOpen] = useState(false);
  const [cloneOrgId, setCloneOrgId] = useState<number | null>(null);
  const [notification, setNotification] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });

  const isBbrAdmin = user?.role?.value === 'ROLE_BBR_ADMIN';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get(URLS.organizationLocation);
        setLocations(createDropdownData(data, ['id', 'name']));
      } catch {
        setLocations([]);
      }
    })();
  }, []);

  useEffect(() => {
    const locationIdFromUrl = searchParams.get('locationId') || '';
    setSelectedLocationId(prev => (prev === locationIdFromUrl ? prev : locationIdFromUrl));
  }, [searchParams]);

  const handleLocationFilterChange = (value: string) => {
    setSelectedLocationId(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set('locationId', value);
    else nextParams.delete('locationId');
    setSearchParams(nextParams, { replace: true });
  };

  const switchOrganizationAndNavigate = useCallback(
    async (orgId: number, orgName: string, redirectTo: string) => {
      const alreadySelected = Number(selectedOrganization?.value) === Number(orgId);
      if (alreadySelected) {
        navigate(redirectTo);
        return;
      }

      try {
        const { status } = await axiosInstance.post(URLS.setOrganizations, { id: orgId });
        if (status === 200) {
          try {
            await dispatch(fetchUserContext() as any);
          } catch {
            // The organization is already switched. Reload still loads context.
          }
          const value = { value: orgId, label: orgName };
          dispatch(setSelectedOrganization(value));
          dispatch(setOrgInfo({ ...org, selectedOrganization: value }));
          navigate(redirectTo);
          setTimeout(() => window.location.reload(), 50);
        }
      } catch {
        // Keep current organization if switch fails
      }
    },
    [dispatch, navigate, org, selectedOrganization?.value],
  );

  const pageConfig: any = useMemo(
    () => ({
      title: strings.organization_title_list,
      subtitle: 'Switch between properties and manage organization details.',
      searchField: 'search',
      searchPlaceholder: 'Search by name, email, or phone',
      AddButtonText: strings.organization_text_add_org,
      AddButtonUrl: routes.addOrganization,
      editButtonUrl: routes.editOrganization,
      listApi: URLS.organization,
      deleteApi: URLS.edit_taxCode,
      addPrivilege: privileges.add_org,
      editPrivilege: privileges.edit_org,
      deletePrivilege: privileges.delete_org,
      customEdit: (id: number) => {
        const row = (pageResponse as any)?.content?.find((item: any) => Number(item.id) === Number(id));
        void switchOrganizationAndNavigate(
          id,
          row?.organizationName || selectedOrganization?.label || '',
          `${routes.editOrganization}/${id}`,
        );
      },
      headerFilters: () => (
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ fontSize: 12 }}>Location</InputLabel>
          <Select
            label="Location"
            value={selectedLocationId}
            onChange={event => handleLocationFilterChange(String(event.target.value))}
            sx={{
              height: 36,
              fontSize: 13,
              bgcolor: '#fff',
              '& .MuiSelect-select': { py: 0.75 },
            }}>
            <MenuItem value="">All locations</MenuItem>
            {locations.map(option => (
              <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      filterChips: selectedLocationId
        ? [
            {
              key: 'locationId',
              label: `Location : ${
                locations.find(option => String(option.value) === selectedLocationId)?.label || selectedLocationId
              }`,
            },
          ]
        : [],
      onClearFilterChip: (key: string) => {
        if (key === 'locationId') handleLocationFilterChange('');
      },
      onClearAllFilterChips: () => handleLocationFilterChange(''),
      tableColumn: [
        {
          field: 'organizationLogo',
          headerName: '',
          flex: 1,
          image: true,
          imageInitialsField: 'organizationName',
        },
        {
          field: 'organizationName',
          headerName: strings.parentOrg_column_name,
          flex: 1.5,
          sortable: true,
          localField: 'localizedAttributes.name',
        },
        {
          field: 'locationName',
          headerName: strings.parentOrg_text_location,
          flex: 1,
          sortable: false,
          renderCell: (value: string) => value || '—',
        },
        {
          field: 'venueCount',
          headerName: strings.venues,
          flex: 0.9,
          sortable: false,
          renderCell: (value: number, row: any) => {
            const count = Number(value) || 0;
            const label = count === 1 ? '1 Venue' : `${count} Venues`;
            return (
              <Typography
                component="span"
                role="button"
                color="primary"
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
                onClick={e => {
                  e.stopPropagation();
                  void switchOrganizationAndNavigate(row.id, row.organizationName, routes.venue);
                }}>
                {label}
              </Typography>
            );
          },
        },
        {
          field: 'email',
          headerName: strings.receivable_field_contact,
          flex: 1.4,
          sortable: false,
          renderCell: (_value: string, row: { email?: string | null; phone?: string | null }) => {
            const email = row.email?.trim();
            const phone = row.phone?.trim();
            if (!email && !phone) {
              return '—';
            }
            return (
              <>
                {email ? (
                  <Typography sx={{ fontSize: '0.8125rem', lineHeight: 1.35 }}>{email}</Typography>
                ) : null}
                {phone ? (
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.35 }}>
                    {phone}
                  </Typography>
                ) : null}
              </>
            );
          },
        },
        lastUpdatedTableColumn(),
      ],
      ...(isBbrAdmin && {
        actionMenuItems: {
          materialIcon: <ContentCopyIcon fontSize="small" color="primary" />,
          buttonText: strings.clone_text,
          privilege: privileges.add_org,
          handleCustomImageMethod: (id: number) => {
            setCloneOrgId(id);
            setCloneDrawerOpen(true);
          },
        },
      }),
    }),
    [
      isBbrAdmin,
      locations,
      pageResponse,
      selectedLocationId,
      selectedOrganization?.label,
      switchOrganizationAndNavigate,
    ],
  );

  const getList = useCallback(
    async (filterData: any) => {
      try {
        setLoading(true);
        const body = filterData
          ? { ...filterData, filter: withEqFilter(filterData.filter, 'locationId', selectedLocationId) }
          : {
              page: index + 1,
              size: pageSize,
              filter: withEqFilter(filter, 'locationId', selectedLocationId),
              sort: ['createdAt:desc'],
            };
        const { data } = await axiosInstance.post(pageConfig.listApi, body);
        setLoading(false);
        setPageResponse(data);
      } catch (error: any) {
        setLoading(false);
      }
    },
    [pageConfig.listApi, index, pageSize, filter, selectedLocationId],
  );

  useEffect(() => {
    (async () => {
      await getList(null);
    })();
  }, [getList]);

  const closeCloneDrawer = () => {
    setCloneDrawerOpen(false);
    setCloneOrgId(null);
  };

  const handleCloneSaved = () => {
    closeCloneDrawer();
    setNotification({
      display: true,
      severity: 'success',
      message: strings.clone_org_success,
    });
    void getList(null);
  };

  return (
    <>
      {notification.display && (
        <Notification
          isOpen={notification.display}
          message={notification.message}
          severity={notification.severity as AlertColor}
          closeNotification={() => setNotification(prev => ({ ...prev, display: false }))}
        />
      )}
      <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />
      <CloneOrganizationDrawer
        open={cloneDrawerOpen}
        organizationId={cloneOrgId}
        onClose={closeCloneDrawer}
        onSaved={handleCloneSaved}
      />
    </>
  );
};

export default OrganizationList;
